import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Sparkles, Loader2, TrendingUp, TrendingDown, ArrowRight, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { formatEUR } from "@/lib/i18n";
import { getCorrectives, getCorrectivesAI } from "@/lib/api";
import SafeMarkdown from "@/components/SafeMarkdown";

const severityStyles = {
  good: "bg-green-50 border-green-200 text-green-900",
  warn: "bg-amber-50 border-amber-200 text-amber-900",
  bad:  "bg-red-50 border-red-200 text-red-900",
  na:   "bg-slate-50 border-slate-200 text-slate-700",
};

const directionIcon = (dir) => {
  if (dir === "reduce") return <TrendingDown className="h-3.5 w-3.5" strokeWidth={2} />;
  if (dir === "grow")   return <TrendingUp className="h-3.5 w-3.5" strokeWidth={2} />;
  return <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />;
};

function BarCompare({ current, target, severity }) {
  const max = Math.max(Math.abs(current || 0), Math.abs(target || 0), 1);
  const cw = Math.min(100, (Math.abs(current || 0) / max) * 100);
  const tw = Math.min(100, (Math.abs(target || 0) / max) * 100);
  const color = severity === "good" ? "bg-green-500" : severity === "warn" ? "bg-amber-500" : severity === "bad" ? "bg-red-500" : "bg-slate-400";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="w-16 text-[10px] font-medium uppercase tracking-wide text-slate-500">Attuale</span>
        <div className="relative h-2 flex-1 rounded-full bg-slate-100">
          <div className={`h-2 rounded-full ${color}`} style={{ width: `${cw}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-16 text-[10px] font-medium uppercase tracking-wide text-slate-500">Target</span>
        <div className="relative h-2 flex-1 rounded-full bg-slate-100">
          <div className="h-2 rounded-full bg-blue-600" style={{ width: `${tw}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function CorrectivesPanel({ reclassified, ratios, sector, onDataChange, includeInPdf, onIncludeInPdfChange }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Load benchmark correctives on mount / when inputs change.
  // Excluded from deps: onDataChange (prop callback — would re-fire on every parent render).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getCorrectives(reclassified, ratios, sector, lang);
        if (!cancelled) {
          setData(res);
          onDataChange?.(res);
        }
      } catch (e) {
        toast.error(t("err_generic"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector, lang, reclassified, ratios, t]);

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await getCorrectivesAI(reclassified, ratios, sector, lang);
      setData(res);
      onDataChange?.(res);
      if (res.ai_error) {
        toast.warning(lang === "it"
          ? "AI temporaneamente non disponibile — mostrati i target da benchmark"
          : "AI temporarily unavailable — showing benchmark targets");
      } else {
        toast.success(t("corr_source_ai"));
      }
    } catch (e) {
      toast.error(t("advisor_error"));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="correctives-loading">
        <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-blue-700" />
        {lang === "it" ? "Calcolo correttivi in corso…" : "Computing correctives…"}
      </div>
    );
  }

  if (!data || !data.items?.length) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900" data-testid="correctives-empty">
        {t("corr_no_ricavi")}
      </div>
    );
  }

  const ricavi = data.ricavi || 0;
  const eb = data.ebitda || {};

  return (
    <div data-testid="correctives-panel">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-blue-50 text-blue-700">
            <Target className="h-4 w-4" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">{t("corr_title")}</div>
            <div className="text-xs text-slate-500">{t("corr_subtitle")}</div>
            <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {data.source === "ai" ? (
                <span className="text-purple-700">✨ {t("corr_source_ai")}</span>
              ) : (
                <span>{t("corr_source_benchmark")}</span>
              )}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={runAi}
          disabled={aiLoading}
          data-testid="correctives-ai-btn"
          className="rounded-full bg-purple-700 text-white hover:bg-purple-800"
        >
          {aiLoading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />}
          {data.source === "ai" ? t("corr_regen_ai") : t("corr_generate_ai")}
        </Button>
      </div>

      {aiLoading && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("corr_generating_ai")}
        </div>
      )}

      {/* EBITDA summary card */}
      {eb.current_pct !== null && eb.current_pct !== undefined && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5" data-testid="corr-ebitda-card">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-800">{t("corr_ebitda_label")}</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <div className="text-[11px] uppercase text-slate-500">{t("corr_col_current")}</div>
              <div className="font-mono text-lg font-semibold text-slate-900">{eb.current_pct?.toFixed(1)}%</div>
              <div className="font-mono text-xs text-slate-500">{formatEUR(eb.current_value, lang)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">{t("corr_col_target")}</div>
              <div className="font-mono text-lg font-semibold text-blue-700">{eb.target_pct?.toFixed(1)}%</div>
              <div className="font-mono text-xs text-slate-500">{formatEUR(eb.target_value, lang)}</div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[11px] uppercase text-slate-500">{t("corr_col_delta")}</div>
              <div className={`font-mono text-lg font-semibold ${eb.delta_value > 0 ? "text-amber-700" : "text-green-700"}`}>
                {eb.delta_value > 0 ? "+" : ""}{formatEUR(eb.delta_value, lang)}
              </div>
              <div className="text-xs text-slate-500">
                {lang === "it" ? "Ricavi di riferimento" : "Reference revenue"}: {formatEUR(ricavi, lang)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.items.map((it) => (
          <div
            key={it.code}
            data-testid={`corr-item-${it.code}`}
            className={`rounded-xl border p-5 ${severityStyles[it.severity] || severityStyles.na}`}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading text-sm font-semibold">{it.label}</div>
                <div className="font-mono text-[10px] uppercase tracking-wide opacity-70">{it.code}</div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-medium">
                {directionIcon(it.direction)}
                {it.direction === "reduce" ? t("corr_delta_reduce") : it.direction === "grow" ? t("corr_delta_grow") : t("corr_delta_keep")}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase opacity-70">{t("corr_col_current")}</div>
                <div className="font-mono text-sm font-semibold">{formatEUR(it.current_value, lang)}</div>
                {it.current_pct !== null && it.current_pct !== undefined && (
                  <div className="font-mono text-[11px] opacity-70">{it.current_pct?.toFixed(1)}%</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase opacity-70">{t("corr_col_target")}</div>
                <div className="font-mono text-sm font-semibold">{formatEUR(it.target_value, lang)}</div>
                {it.target_pct !== null && it.target_pct !== undefined && (
                  <div className="font-mono text-[11px] opacity-70">{it.target_pct?.toFixed(1)}%</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase opacity-70">{t("corr_col_delta")}</div>
                <div className="font-mono text-sm font-semibold">{formatEUR(it.delta_value, lang)}</div>
              </div>
            </div>
            <BarCompare current={it.current_value} target={it.target_value} severity={it.severity} />
            {it.suggestion && (
              <div className="mt-3 border-t border-black/10 pt-3 text-xs leading-relaxed">
                {it.suggestion}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI commentary (if any) */}
      {data.commentary && (
        <div className="mt-6 rounded-xl border border-purple-200 bg-purple-50/40 p-5" data-testid="corr-ai-commentary">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-purple-800">
            <Sparkles className="h-3.5 w-3.5" /> {lang === "it" ? "Commento AI" : "AI Commentary"}
          </div>
          <div className="space-y-1"><SafeMarkdown text={data.commentary} testId="corr-ai-commentary-md" /></div>
        </div>
      )}

      {/* Include in PDF */}
      <div className="mt-6 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white text-blue-700 ring-1 ring-blue-200">
            <FileText className="h-4 w-4" strokeWidth={2} />
          </div>
          <div>
            <Label htmlFor="corr-include-pdf-switch" className="cursor-pointer text-sm font-medium text-slate-900">
              {t("corr_include_pdf")}
            </Label>
            <div className="text-xs text-slate-500">
              {lang === "it" ? "I correttivi saranno inclusi nel PDF esportato" : "The correctives will be appended to the exported PDF"}
            </div>
          </div>
        </div>
        <Switch
          id="corr-include-pdf-switch"
          data-testid="corr-include-pdf-switch"
          checked={!!includeInPdf}
          onCheckedChange={(v) => onIncludeInPdfChange?.(v)}
        />
      </div>
    </div>
  );
}
